import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SettingsIcon from "@mui/icons-material/Settings";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Normalise EligibleWorkflow object → "group/name" path string */
const toPath = (wf) => `${wf.workflowGroup}/${wf.workflowName}`;

/** Normalise "group/name" path string → EligibleWorkflow object */
const fromPath = (path) => {
  const [workflowGroup, ...rest] = path.split("/");
  return { workflowGroup, workflowName: rest.join("/") };
};

/**
 * Takes a flat string[] of "group/workflowName" entries and returns
 * { [group]: string[] } sorted alphabetically within each group.
 */
function groupWorkflowItems(items) {
  const grouped = {};
  items.forEach((wf) => {
    const [group, ...nameParts] = String(wf).split("/");
    const wfName = nameParts.join("/");
    if (!group || !wfName) return;
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(wfName);
  });
  Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.localeCompare(b)));
  return grouped;
}

// ---------------------------------------------------------------------------
// SimpleTreePanel — renders one panel (available OR assigned) as grouped tree
// ---------------------------------------------------------------------------

function SimpleTreePanel({ title, groupedItems, iconType, onToggleItem, onToggleGroup }) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const groupKeys = Object.keys(groupedItems);

  // Collapse all groups whenever the data set changes
  useEffect(() => {
    const next = {};
    groupKeys.forEach((g) => { next[g] = false; });
    setExpandedGroups(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKeys.join("|")]);

  const toggleExpand = (group) =>
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));

  const actionButtonSx = {
    p: 0,
    m: 0,
    border: 0,
    background: "transparent",
    color: "#69707d",
    cursor: "pointer",
    lineHeight: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 1, borderColor: "#cfd5df", overflow: "hidden", bgcolor: "#fff" }}
    >
      {/* Panel header */}
      <Box sx={{ px: 1.25, py: 0.85, borderBottom: "1px solid #dde2ea", bgcolor: "#edf0f5" }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.78rem",
            color: "#616a79",
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ height: 260, maxHeight: 260, overflow: "auto", p: 0.75 }}>
        {groupKeys.length === 0 && (
          <Typography variant="caption" sx={{ color: "#9aa0ae", p: 0.5, display: "block" }}>
            None
          </Typography>
        )}

        {Object.entries(groupedItems).map(([group, names]) => (
          <Box key={group} sx={{ mb: 0.45 }}>
            {/* Group row */}
            <Box
              sx={{
                px: 0.4,
                minHeight: 26,
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                color: "#5b6473",
                userSelect: "none",
              }}
            >
              {/* Add/Remove entire group */}
              <Box
                component="button"
                type="button"
                onClick={() => onToggleGroup(group, names)}
                title={iconType === "add" ? "Add entire folder" : "Remove entire folder"}
                sx={actionButtonSx}
              >
                {iconType === "add" ? (
                  <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                ) : (
                  <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                )}
              </Box>

              {/* Expand/collapse folder icon */}
              <Box
                sx={{ width: 16, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}
              >
                <FolderOpenIcon
                  onClick={() => toggleExpand(group)}
                  sx={{ fontSize: 16, color: "#7b8493", cursor: "pointer" }}
                />
              </Box>

              {/* Group name */}
              <Typography
                variant="caption"
                onClick={() => toggleExpand(group)}
                sx={{ fontWeight: 700, lineHeight: 1.2, cursor: "pointer" }}
              >
                {group}
              </Typography>
            </Box>

            {/* Workflow items (collapsed by default) */}
            <Box sx={{ mt: 0.15, pl: 1.2, display: expandedGroups[group] ? "block" : "none" }}>
              {names.map((name) => (
                <Box
                  key={`${group}/${name}`}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    color: "#4f5867",
                    px: 0.4,
                    py: 0.2,
                    minHeight: 26,
                    borderRadius: 0.6,
                    "&:hover": { bgcolor: "#f7f9fc" },
                  }}
                >
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    {/* Add/Remove single item */}
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onToggleItem(`${group}/${name}`)}
                      title={iconType === "add" ? "Add item" : "Remove item"}
                      sx={actionButtonSx}
                    >
                      {iconType === "add" ? (
                        <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                      )}
                    </Box>

                    <Box
                      sx={{
                        width: 16,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: 14, color: "#8a93a2" }} />
                    </Box>

                    <Typography variant="body2" sx={{ textAlign: "left", fontSize: "0.82rem" }}>
                      {name}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// WorkflowAssignmentPanel — public reusable component
// ---------------------------------------------------------------------------

export default function WorkflowAssignmentPanel({
  availableWorkflows,
  assignedWorkflows,
  onChange,
  loading,
}) {
  // Normalise objects → path strings for internal use
  const allPaths = availableWorkflows.map(toPath);
  const assignedPaths = assignedWorkflows.map(toPath);
  const unassignedPaths = allPaths.filter((p) => !assignedPaths.includes(p));

  const handleAddItem = (path) => {
    onChange([...assignedWorkflows, fromPath(path)]);
  };

  const handleRemoveItem = (path) => {
    onChange(assignedWorkflows.filter((wf) => toPath(wf) !== path));
  };

  const handleAddGroup = (group, names) => {
    const newPaths = names.map((n) => `${group}/${n}`);
    const merged = Array.from(new Set([...assignedPaths, ...newPaths])).sort((a, b) =>
      a.localeCompare(b)
    );
    onChange(merged.map(fromPath));
  };

  const handleRemoveGroup = (group, names) => {
    const toRemove = new Set(names.map((n) => `${group}/${n}`));
    onChange(assignedWorkflows.filter((wf) => !toRemove.has(toPath(wf))));
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <Skeleton variant="rounded" height={300} />
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
      <SimpleTreePanel
        title="Available Workflows"
        groupedItems={groupWorkflowItems(unassignedPaths)}
        iconType="add"
        onToggleItem={handleAddItem}
        onToggleGroup={handleAddGroup}
      />
      <SimpleTreePanel
        title="Assigned Workflows"
        groupedItems={groupWorkflowItems(assignedPaths)}
        iconType="remove"
        onToggleItem={handleRemoveItem}
        onToggleGroup={handleRemoveGroup}
      />
    </Box>
  );
}

WorkflowAssignmentPanel.propTypes = {
  availableWorkflows: PropTypes.arrayOf(
    PropTypes.shape({
      workflowGroup: PropTypes.string.isRequired,
      workflowName: PropTypes.string.isRequired,
    })
  ).isRequired,
  assignedWorkflows: PropTypes.arrayOf(
    PropTypes.shape({
      workflowGroup: PropTypes.string.isRequired,
      workflowName: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};
